import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { useProfile } from "@/lib/ProfileContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ShareModal from "@/components/ShareModal";
import OnboardingGuide from "@/components/OnboardingGuide";
import SetupBanner from "@/components/SetupBanner";
import GmailPermissionBanner from "@/components/GmailPermissionBanner";
import AutoPilotStatusWidget from "@/components/AutoPilotStatusWidget";
import AutoPilotSetup from "@/components/AutoPilotSetup";
import { 
  Plus, 
  Calendar, 
  BarChart3, 
  FileText, 
  Video, 
  Settings, 
  LogOut,
  Zap,
  Clock,
  TrendingUp,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Share2,
  Image,
  Link2,
  Gift,
  HelpCircle,
  Plug,
  Sparkles,
  Heart,
  MessageCircle,
  Send,
  Target,
  Lightbulb,
  ArrowUpRight,
  Filter,
  X,
  Tag,
  AlertTriangle,
  Bell,
  Shield,
  Building2,
  CreditCard,
  Users,
  PenSquare,
  Layout,
  UserCircle
} from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import PWAInstallBanner from "@/components/PWAInstallBanner";

export default function DashboardPage() {
  const { user, updateUser } = useAuth();
  const { activeProfile, refreshProfiles } = useProfile();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showAutoPilotSetup, setShowAutoPilotSetup] = useState(false);
  const [engagementData, setEngagementData] = useState(null);
  const [engagementLoading, setEngagementLoading] = useState(true);
  const [socialPerformance, setSocialPerformance] = useState(null);
  const [socialPerformanceLoading, setSocialPerformanceLoading] = useState(true);
  const [postsExpanded, setPostsExpanded] = useState(false);
  const [postsSortBy, setPostsSortBy] = useState("engagement"); // "engagement", "likes", "comments", "date"
  const [postsPage, setPostsPage] = useState(1);
  const POSTS_PER_PAGE = 5;
  
  // Promotions and Announcements
  const [activePromotion, setActivePromotion] = useState(null);
  const [pendingAnnouncement, setPendingAnnouncement] = useState(null);
  const [policyRequired, setPolicyRequired] = useState(null);
  const [promoBannerDismissed, setPromoBannerDismissed] = useState(false);
  
  // Profile & Plan Status
  const [profileData, setProfileData] = useState(null);
  const [creditSummary, setCreditSummary] = useState(null);
  const [activePlans, setActivePlans] = useState([]);

  // Re-fetch dashboard data when active profile changes
  useEffect(() => {
    if (activeProfile?.id) {
      fetchDashboardData();
      fetchEngagementData();
      fetchSocialPerformance();
      fetchProfileAndPlanStatus();
    }
  }, [activeProfile?.id]);

  useEffect(() => {
    fetchDashboardData();
    fetchEngagementData();
    fetchSocialPerformance();
    fetchPromotionsAndAnnouncements();
    fetchProfileAndPlanStatus();
    
    // Check if user needs onboarding (ONLY for brand new users)
    // Tour shows only if: localStorage not set AND user was created in last 24 hours
    const onboardingComplete = localStorage.getItem("viraly_onboarding_complete");
    if (!onboardingComplete && user) {
      const userCreatedAt = user.created_at ? new Date(user.created_at) : null;
      const now = new Date();
      const hoursSinceCreation = userCreatedAt ? (now - userCreatedAt) / (1000 * 60 * 60) : 999;
      
      // Only show tour for users created within last 24 hours
      if (hoursSinceCreation < 24) {
        setTimeout(() => setShowOnboarding(true), 500);
      } else {
        // Mark as complete for returning users so it doesn't check again
        localStorage.setItem("viraly_onboarding_complete", "true");
      }
    }
  }, [user]);

  const fetchProfileAndPlanStatus = async () => {
    try {
      const [profilesRes, creditsRes, plansRes] = await Promise.all([
        apiClient.get("/users/profiles").catch(() => null),
        apiClient.get("/users/credit-summary").catch(() => null),
        apiClient.get("/users/plans").catch(() => null)
      ]);
      
      if (profilesRes?.data) setProfileData(profilesRes.data);
      if (creditsRes?.data) setCreditSummary(creditsRes.data);
      if (plansRes?.data?.plans) setActivePlans(plansRes.data.plans);
    } catch (err) {
      console.log("Failed to fetch profile/plan status");
    }
  };
  
  // Switch active profile - uses ProfileContext for consistent state
  const handleSwitchProfile = async (profileId) => {
    try {
      await apiClient.post(`/users/profiles/${profileId}/activate`);
      toast.success("Profile switched successfully");
      // Refresh ProfileContext state
      refreshProfiles();
      // Refresh dashboard data
      fetchProfileAndPlanStatus();
      fetchDashboardData();
    } catch (err) {
      console.error("Failed to switch profile:", err);
      toast.error("Failed to switch profile");
    }
  };

  const fetchPromotionsAndAnnouncements = async () => {
    try {
      // Fetch active promotions
      const promosRes = await apiClient.get("/promotions/active").catch(() => null);
      if (promosRes?.data?.promotions?.length > 0) {
        // Get the first active promotion for banner
        setActivePromotion(promosRes.data.promotions[0]);
      }
      
      // Fetch pending announcements
      const annRes = await apiClient.get("/announcements/pending").catch(() => null);
      if (annRes?.data) {
        if (annRes.data.policy_required) {
          setPolicyRequired(annRes.data.policy_required);
        }
        if (annRes.data.announcements?.length > 0) {
          setPendingAnnouncement(annRes.data.announcements[0]);
        }
      }
    } catch (err) {
      console.log("Failed to fetch promotions/announcements");
    }
  };

  const handleDismissPromotion = () => {
    setPromoBannerDismissed(true);
    // Store in session so it doesn't show again this session
    sessionStorage.setItem(`promo_dismissed_${activePromotion?.id}`, "true");
  };

  const handleDismissAnnouncement = async (announcementId) => {
    try {
      await apiClient.post(`/announcements/${announcementId}/dismiss`);
      setPendingAnnouncement(null);
      toast.success("Announcement dismissed");
    } catch (err) {
      toast.error("Failed to dismiss announcement");
    }
  };

  const handleAcceptPolicy = async () => {
    if (!policyRequired) return;
    try {
      await apiClient.post(`/announcements/${policyRequired.id}/accept`);
      setPolicyRequired(null);
      toast.success("Policy accepted");
    } catch (err) {
      toast.error("Failed to accept policy");
    }
  };

  const fetchDashboardData = async () => {
    try {
      const profileParam = activeProfile?.id ? `?profile_id=${activeProfile.id}` : '';
      const [statsRes, activityRes] = await Promise.all([
        apiClient.get("/users/stats"),
        apiClient.get(`/users/activity${profileParam}`)
      ]);
      setStats(statsRes.data);
      setActivity(activityRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchEngagementData = async () => {
    try {
      const res = await apiClient.get("/content/posts/engagement-summary");
      setEngagementData(res.data);
    } catch (error) {
      console.log("Engagement data not available");
    } finally {
      setEngagementLoading(false);
    }
  };
  
  const fetchSocialPerformance = async () => {
    try {
      setSocialPerformanceLoading(true);
      const res = await apiClient.get("/content/analytics/social-performance");
      setSocialPerformance(res.data);
    } catch (error) {
      console.log("Social performance data not available");
    } finally {
      setSocialPerformanceLoading(false);
    }
  };

  const handleShare = () => {
    // Open the ShareModal with referral info
    setShowShareModal(true);
  };

  // Stats matching the landing page preview
  const statCards = [
    {
      title: "Posts Queued",
      value: stats?.posts_queued || 0,
      icon: Clock,
      gradient: true
    },
    {
      title: "Reach This Month",
      value: stats?.reach_this_month?.toLocaleString() || "0",
      icon: TrendingUp,
      color: "text-viraly-success"
    },
    {
      title: "Posts Remaining",
      value: creditSummary?.total_posts ?? stats?.posts_remaining ?? 0,
      icon: FileText,
      color: "text-viraly-orange",
      subtext: (creditSummary?.total_posts ?? stats?.posts_remaining ?? 0) < 20 ? "Running low - Buy more" : null,
      onClick: () => navigate("/pricing?tab=payg&item=posts")
    },
    {
      title: "Viral Score Avg",
      value: stats?.viral_score_avg > 0 ? `${stats.viral_score_avg}%` : "—",
      icon: Zap,
      color: "text-purple-400"
    }
  ];

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        {/* PWA Install Banner - Shows on dashboard for mobile users */}
        <PWAInstallBanner />
        
        {/* Gmail Permission Banner - Shows after Google login if email permissions needed */}
        <GmailPermissionBanner />
        
        {/* Setup Banner - Brand + Social Accounts */}
        <SetupBanner />
        
        {/* Policy Acceptance Modal - BLOCKING */}
        <AnimatePresence>
          {policyRequired && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4"
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                className="card-base p-6 max-w-lg w-full"
              >
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-yellow-400" />
                  <h2 className="text-xl font-bold">{policyRequired.title}</h2>
                </div>
                <p className="text-viraly-muted mb-4">{policyRequired.message}</p>
                <p className="text-sm text-viraly-muted mb-4">
                  Please review our updated policies before continuing:
                </p>
                <div className="flex gap-4 mb-6">
                  <a 
                    href="/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-viraly-orange hover:underline flex items-center gap-1 text-sm"
                  >
                    <FileText className="w-4 h-4" />
                    Terms of Service
                  </a>
                  <a 
                    href="/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-viraly-orange hover:underline flex items-center gap-1 text-sm"
                  >
                    <Shield className="w-4 h-4" />
                    Privacy Policy
                  </a>
                </div>
                <p className="text-xs text-viraly-muted mb-4">
                  Version: {policyRequired.policy_version || "1.0"}
                </p>
                <Button onClick={handleAcceptPolicy} className="btn-primary w-full">
                  I Accept
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Promotion Banner */}
        {activePromotion && !promoBannerDismissed && !sessionStorage.getItem(`promo_dismissed_${activePromotion.id}`) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-xl bg-gradient-to-r from-viraly-orange/20 to-purple-500/20 border border-viraly-orange/30 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <Tag className="w-5 h-5 text-viraly-orange" />
              <div>
                <p className="font-medium text-viraly-orange">{activePromotion.name}</p>
                <p className="text-sm text-viraly-muted">
                  {activePromotion.discount_type === "percentage" ? `${activePromotion.discount_value}% off` : `$${activePromotion.discount_value} off`}
                  {activePromotion.message && ` - ${activePromotion.message}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-viraly-muted">
                Ends {new Date(activePromotion.end_date).toLocaleDateString()}
              </span>
              <button onClick={handleDismissPromotion} className="p-1 hover:bg-white/10 rounded">
                <X className="w-4 h-4 text-viraly-muted" />
              </button>
            </div>
          </motion.div>
        )}

        {/* Announcement Banner (non-blocking) - Only show if not a policy update */}
        {pendingAnnouncement && !policyRequired && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-xl flex items-center justify-between ${
              pendingAnnouncement.priority === "high" 
                ? "bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30"
                : "bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30"
            }`}
          >
            <div className="flex items-center gap-3">
              <Bell className={`w-5 h-5 ${pendingAnnouncement.priority === "high" ? "text-red-400" : "text-blue-400"}`} />
              <div>
                <p className={`font-medium ${pendingAnnouncement.priority === "high" ? "text-red-400" : "text-blue-400"}`}>
                  {pendingAnnouncement.title}
                  {pendingAnnouncement.priority === "high" && (
                    <span className="ml-2 px-2 py-0.5 text-[10px] bg-red-500 text-white rounded-full">Important</span>
                  )}
                </p>
                <p className="text-sm text-viraly-muted">{pendingAnnouncement.message}</p>
              </div>
            </div>
            <Button 
              onClick={() => handleDismissAnnouncement(pendingAnnouncement.id)}
              size="sm"
              variant="ghost"
              className={pendingAnnouncement.priority === "high" ? "text-red-400 hover:text-red-300" : "text-blue-400 hover:text-blue-300"}
            >
              Got it
            </Button>
          </motion.div>
        )}
        
        {/* Persistent reminder for users who skipped social setup */}
        {user?.skipped_social_setup && !user?.social_connections?.facebook?.connected && 
         !user?.social_connections?.instagram?.connected && !user?.social_connections?.tiktok?.connected && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between gap-4 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6"
            data-testid="connect-social-reminder"
          >
            <div className="flex items-center gap-3">
              <Plug className="w-5 h-5 text-blue-400" />
              <span className="text-sm">
                <span className="text-blue-400 font-medium">Connect your social accounts</span>
                <span className="text-viraly-muted"> to unlock scheduling & auto-posting</span>
              </span>
            </div>
            <Button 
              onClick={() => navigate("/settings?tab=social")}
              size="sm"
              variant="ghost"
              className="text-blue-400 hover:text-blue-300"
            >
              Connect Now
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </motion.div>
        )}
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold mb-1">
              Welcome back, <span className="gradient-text">{user?.name?.split(" ")[0]}</span>
            </h1>
            <p className="text-viraly-muted">Here's what's happening with your content</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost"
              onClick={() => setShowOnboarding(true)}
              className="text-viraly-muted hover:text-white"
              title="Show Tour"
              data-testid="show-tour-btn"
            >
              <HelpCircle className="w-5 h-5" />
            </Button>
            <Button 
              variant="ghost"
              onClick={handleShare}
              className="text-viraly-muted hover:text-white"
              data-testid="share-btn"
            >
              <Share2 className="w-5 h-5" />
            </Button>
            <Button 
              onClick={() => navigate("/create")}
              className="btn-primary px-6 py-3 flex items-center gap-2"
              data-testid="create-post-btn"
            >
              <Plus className="w-5 h-5" />
              Create New Post
            </Button>
          </div>
        </div>

        {/* Stats Grid - Matches landing page preview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat, i) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.1 }}
              className={`stat-card ${stat.onClick ? 'cursor-pointer hover:ring-1 hover:ring-viraly-orange/50 transition-all' : ''}`}
              data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={stat.onClick}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  stat.gradient ? "bg-viraly-gradient" : "bg-white/5"
                }`}>
                  <stat.icon className={`w-5 h-5 ${stat.gradient ? "text-white" : stat.color}`} />
                </div>
                {stat.onClick && (
                  <ChevronRight className="w-4 h-4 text-viraly-muted" />
                )}
              </div>
              <p className="text-viraly-muted text-sm mb-1">{stat.title}</p>
              <p className={`text-3xl font-heading font-bold ${stat.gradient ? "gradient-text" : stat.color}`}>
                {loading ? "..." : stat.value}
              </p>
              {stat.subtext && (
                <p className="text-xs text-viraly-orange mt-1 animate-pulse">{stat.subtext}</p>
              )}
            </motion.div>
          ))}
        </div>
        
        {/* Auto-Pilot Status Widget */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.12 }}
          className="mb-8"
        >
          <AutoPilotStatusWidget 
            onConfigure={() => setShowAutoPilotSetup(true)} 
          />
        </motion.div>
        
        {/* Auto-Pilot Setup Modal */}
        <AnimatePresence>
          {showAutoPilotSetup && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowAutoPilotSetup(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-viraly-dark border border-white/10 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-viraly-light">Configure Auto-Pilot</h2>
                  <Button variant="ghost" size="sm" onClick={() => setShowAutoPilotSetup(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <AutoPilotSetup 
                  onClose={() => setShowAutoPilotSetup(false)}
                  onActivated={() => {
                    setShowAutoPilotSetup(false);
                    toast.success("Auto-Pilot activated!");
                  }}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Profile & Plan Status Card - Prominent display of multi-profile and plan info */}
        {(profileData || creditSummary) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
            className="mb-8 card-base p-5"
            data-testid="profile-plan-status"
          >
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Profiles Section */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-viraly-orange" />
                    <h3 className="font-heading font-bold">Profiles</h3>
                    <span className="text-xs px-2 py-0.5 bg-viraly-orange/20 text-viraly-orange rounded-full">
                      {profileData?.profiles?.length || 0} / {profileData?.profile_limit || 1}
                    </span>
                  </div>
                  {profileData?.can_create_more && (
                    <Button
                      onClick={() => navigate("/settings?tab=profile")}
                      size="sm"
                      variant="ghost"
                      className="text-viraly-orange hover:text-white text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Profile
                    </Button>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {profileData?.profiles?.map((profile, idx) => (
                    <button
                      key={profile.id || idx}
                      onClick={() => !profile.is_default && handleSwitchProfile(profile.id)}
                      disabled={profile.is_default}
                      className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-2 transition-all ${
                        profile.is_default 
                          ? 'bg-viraly-orange/10 border-viraly-orange/30 text-viraly-orange cursor-default' 
                          : 'bg-white/5 border-white/10 text-viraly-muted hover:border-viraly-orange/50 hover:bg-viraly-orange/5 hover:text-viraly-light cursor-pointer'
                      }`}
                      data-testid={`profile-card-${idx}`}
                    >
                      {profile.headshot_url ? (
                        <img 
                          src={profile.headshot_url} 
                          alt={profile.name}
                          className="w-5 h-5 rounded-full object-cover"
                        />
                      ) : (
                        <Users className="w-3.5 h-3.5" />
                      )}
                      <span className="max-w-[150px] truncate">{profile.name}</span>
                      {profile.is_default && (
                        <span className="text-[10px] uppercase opacity-60">Active</span>
                      )}
                    </button>
                  ))}
                  
                  {/* Profile slots available indicator */}
                  {profileData?.can_create_more ? (
                    <button
                      onClick={() => navigate("/settings?tab=profile")}
                      className="px-3 py-2 rounded-lg border border-dashed border-white/20 text-sm flex items-center gap-2 text-viraly-muted hover:border-viraly-orange/50 hover:text-viraly-orange transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>{profileData.profile_limit - (profileData.profiles?.length || 0)} more available</span>
                    </button>
                  ) : profileData?.profiles?.length > 0 && (
                    <div className="px-3 py-2 rounded-lg bg-white/5 text-xs text-viraly-muted flex items-center gap-1">
                      <span>Maximum profiles reached</span>
                      {profileData?.profile_limit < 5 && (
                        <button
                          onClick={() => navigate("/pricing")}
                          className="text-viraly-orange hover:underline ml-1"
                        >
                          Upgrade for more
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Vertical Divider */}
              <div className="hidden lg:block w-px bg-white/10"></div>
              
              {/* Active Plans Section */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-viraly-success" />
                    <h3 className="font-heading font-bold">Active Plans</h3>
                    <span className="text-xs px-2 py-0.5 bg-viraly-success/20 text-viraly-success rounded-full">
                      {activePlans?.length || 0} active
                    </span>
                  </div>
                  <Button
                    onClick={() => navigate("/pricing")}
                    size="sm"
                    variant="ghost"
                    className="text-viraly-success hover:text-white text-xs"
                  >
                    Add Plan
                    <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
                
                {activePlans?.length > 0 ? (
                  <div className="space-y-2">
                    {activePlans.slice(0, 3).map((plan, idx) => (
                      <div
                        key={plan.id || idx}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                          plan.days_remaining <= 7 
                            ? 'bg-red-500/10 border border-red-500/30' 
                            : plan.days_remaining <= 14
                              ? 'bg-yellow-500/10 border border-yellow-500/30'
                              : 'bg-white/5'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{plan.plan_name}</span>
                          {plan.video_quality && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-viraly-orange/20 text-viraly-orange rounded">
                              {plan.video_quality}
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <span className={`text-xs font-medium ${
                            plan.days_remaining <= 7 ? 'text-red-400' :
                            plan.days_remaining <= 14 ? 'text-yellow-400' : 'text-viraly-success'
                          }`}>
                            {plan.days_remaining}d left
                          </span>
                        </div>
                      </div>
                    ))}
                    {activePlans.length > 3 && (
                      <button
                        onClick={() => navigate("/settings?tab=billing")}
                        className="text-xs text-viraly-muted hover:text-viraly-orange w-full text-center py-1"
                      >
                        +{activePlans.length - 3} more plan{activePlans.length - 3 !== 1 ? 's' : ''} →
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-viraly-muted text-sm">
                    <p className="mb-2">No active plans</p>
                    <Button onClick={() => navigate("/pricing")} size="sm" className="btn-primary">
                      Get Started
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Vertical Divider */}
              <div className="hidden lg:block w-px bg-white/10"></div>
              
              {/* Quick Credits Summary */}
              <div className="lg:w-48">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-5 h-5 text-purple-400" />
                  <h3 className="font-heading font-bold">Credits</h3>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-viraly-muted">Posts</span>
                    <span className="font-bold gradient-text">{creditSummary?.total_posts || user?.posts_remaining || 0}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-viraly-muted">Videos</span>
                    <span className="font-bold text-viraly-success">{creditSummary?.total_video_credits || user?.video_credits_remaining || 0}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-viraly-muted">Regen</span>
                    <span className="font-bold text-blue-400">{creditSummary?.total_regen_credits || user?.regen_credits || 0}</span>
                  </div>
                  
                  <button
                    onClick={() => navigate("/settings?tab=billing")}
                    className="mt-2 w-full text-xs text-viraly-muted hover:text-viraly-orange flex items-center justify-center gap-1"
                  >
                    View details <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Social Performance Analytics - Real-time data from bundle.social */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="mb-8"
          data-testid="social-performance-analytics"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold">Social Performance</h2>
                <p className="text-sm text-viraly-muted">Last 30 days engagement across platforms</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              onClick={fetchSocialPerformance}
              className="text-viraly-muted hover:text-white"
              disabled={socialPerformanceLoading}
              data-testid="refresh-social-performance"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${socialPerformanceLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          {socialPerformanceLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="card-base p-4 animate-pulse">
                  <div className="h-4 bg-white/10 rounded w-20 mb-2"></div>
                  <div className="h-8 bg-white/10 rounded w-16"></div>
                </div>
              ))}
            </div>
          ) : socialPerformance?.summary ? (
            <div className="space-y-4">
              {/* Summary Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="card-base p-4" data-testid="total-posts-metric">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-viraly-muted">Posts</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-400">{socialPerformance.summary.total_posts}</p>
                  <p className="text-xs text-viraly-muted mt-1">Last 30 days</p>
                </div>
                <div className="card-base p-4" data-testid="total-engagement-metric">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="w-4 h-4 text-pink-400" />
                    <span className="text-sm text-viraly-muted">Engagement</span>
                  </div>
                  <p className="text-2xl font-bold text-pink-400">{socialPerformance.summary.total_engagement.toLocaleString()}</p>
                  <p className="text-xs text-viraly-muted mt-1">Likes, comments, shares</p>
                </div>
                <div className="card-base p-4" data-testid="followers-metric">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-viraly-muted">Followers</span>
                  </div>
                  <p className="text-2xl font-bold text-green-400">{socialPerformance.summary.total_followers.toLocaleString()}</p>
                  <p className="text-xs text-viraly-muted mt-1">Total reach</p>
                </div>
                <div className="card-base p-4" data-testid="engagement-rate-metric">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-viraly-orange" />
                    <span className="text-sm text-viraly-muted">Eng. Rate</span>
                  </div>
                  <p className="text-2xl font-bold text-viraly-orange">{socialPerformance.summary.engagement_rate}%</p>
                  <p className="text-xs text-viraly-muted mt-1">{socialPerformance.summary.engagement_rate >= 3 ? 'Excellent!' : socialPerformance.summary.engagement_rate >= 1 ? 'Good' : 'Room to grow'}</p>
                </div>
                <div className="card-base p-4 col-span-2 md:col-span-1" data-testid="impressions-metric">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-viraly-muted">Impressions</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-400">{socialPerformance.summary.total_impressions.toLocaleString()}</p>
                  <p className="text-xs text-viraly-muted mt-1">Content views</p>
                </div>
              </div>
              
              {/* Platform Breakdown */}
              {socialPerformance.platforms?.length > 0 && (
                <div className="card-base p-4">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Plug className="w-4 h-4 text-viraly-muted" />
                    Platform Breakdown
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {socialPerformance.platforms.map((platform, idx) => (
                      <div 
                        key={idx} 
                        className="p-3 rounded-lg bg-white/5 border border-white/10"
                        data-testid={`platform-${platform.platform}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium capitalize">{platform.platform}</span>
                          <span className="text-xs text-viraly-muted">{platform.posts_count} posts</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-viraly-muted">Engagement</span>
                          <span className="font-bold text-pink-400">{platform.engagement.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-viraly-muted">Followers</span>
                          <span className="font-bold text-green-400">{platform.followers.toLocaleString()}</span>
                        </div>
                        {platform.top_post && (
                          <div className="mt-2 pt-2 border-t border-white/10">
                            <p className="text-xs text-viraly-muted mb-1">Top Post:</p>
                            <p className="text-xs truncate">{platform.top_post.caption}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* AI Insights */}
              {socialPerformance.insights?.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {socialPerformance.insights.map((insight, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className={`p-4 rounded-xl border ${
                        insight.type === 'success' ? 'bg-green-500/10 border-green-500/30' :
                        insight.type === 'improve' ? 'bg-orange-500/10 border-orange-500/30' :
                        'bg-blue-500/10 border-blue-500/30'
                      }`}
                      data-testid={`insight-${idx}`}
                    >
                      <div className="flex items-start gap-2">
                        {insight.icon === 'trending-up' && <TrendingUp className={`w-5 h-5 ${insight.type === 'success' ? 'text-green-400' : 'text-blue-400'}`} />}
                        {insight.icon === 'target' && <Target className="w-5 h-5 text-blue-400" />}
                        {insight.icon === 'lightbulb' && <Lightbulb className="w-5 h-5 text-orange-400" />}
                        <p className="text-sm">{insight.message}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="card-base p-8 text-center">
              <BarChart3 className="w-12 h-12 text-viraly-muted mx-auto mb-3" />
              <p className="text-viraly-muted mb-2">No analytics data yet</p>
              <p className="text-sm text-viraly-muted">Publish some posts to see your performance metrics</p>
              <Button 
                onClick={() => navigate("/create")}
                className="btn-primary mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Content
              </Button>
            </div>
          )}
        </motion.div>

        {/* Engagement Analytics Section - HIDDEN until we have social media read permissions
            NOTE: This section requires Instagram Insights API and Facebook Page Insights API 
            read permissions to display real engagement data (likes, comments, shares).
            Currently hidden to show a clean dashboard. Re-enable when permissions are granted.
        */}
        {false && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="mb-8"
            data-testid="engagement-analytics"
          >
            <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold">Posts Performance</h2>
                <p className="text-sm text-viraly-muted">Engagement across your Viraly-created content</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              onClick={fetchEngagementData}
              className="text-viraly-muted hover:text-white"
              disabled={engagementLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${engagementLoading ? 'animate-spin' : ''}`} />
              Sync
            </Button>
          </div>
          
          {engagementLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="card-base p-4 animate-pulse">
                  <div className="h-4 bg-white/10 rounded w-20 mb-2"></div>
                  <div className="h-8 bg-white/10 rounded w-16"></div>
                </div>
              ))}
            </div>
          ) : engagementData?.summary ? (
            <div className="space-y-4">
              {/* Engagement Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="card-base p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="w-4 h-4 text-pink-400" />
                    <span className="text-sm text-viraly-muted">Total Likes</span>
                  </div>
                  <p className="text-2xl font-bold text-pink-400">{engagementData.summary.total_likes.toLocaleString()}</p>
                </div>
                <div className="card-base p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-viraly-muted">Comments</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-400">{engagementData.summary.total_comments.toLocaleString()}</p>
                </div>
                <div className="card-base p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Send className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-viraly-muted">Shares</span>
                  </div>
                  <p className="text-2xl font-bold text-green-400">{engagementData.summary.total_shares.toLocaleString()}</p>
                </div>
                <div className="card-base p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-viraly-orange" />
                    <span className="text-sm text-viraly-muted">Avg. Engagement</span>
                  </div>
                  <p className="text-2xl font-bold text-viraly-orange">{engagementData.summary.avg_engagement}</p>
                </div>
              </div>
              
              {/* AI Insights */}
              {engagementData.ai_insights && engagementData.ai_insights.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {engagementData.ai_insights.map((insight, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className={`p-4 rounded-xl border ${
                        insight.type === 'success' ? 'bg-green-500/10 border-green-500/30' :
                        insight.type === 'improve' ? 'bg-orange-500/10 border-orange-500/30' :
                        insight.type === 'tip' ? 'bg-blue-500/10 border-blue-500/30' :
                        'bg-white/5 border-white/10'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Lightbulb className={`w-5 h-5 mt-0.5 ${
                          insight.type === 'success' ? 'text-green-400' :
                          insight.type === 'improve' ? 'text-orange-400' :
                          insight.type === 'tip' ? 'text-blue-400' :
                          'text-viraly-muted'
                        }`} />
                        <div className="flex-1">
                          <p className="font-medium text-sm mb-1">{insight.title}</p>
                          <p className="text-xs text-viraly-muted mb-2">{insight.message}</p>
                          {insight.type === 'success' && engagementData.top_posts?.[0] && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/create?ref=${engagementData.top_posts[0].id}`)}
                              className="text-green-400 hover:text-green-300 p-0 h-auto text-xs"
                            >
                              Create Similar <ArrowUpRight className="w-3 h-3 ml-1" />
                            </Button>
                          )}
                          {insight.type === 'improve' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate("/tools/viral-analyzer")}
                              className="text-orange-400 hover:text-orange-300 p-0 h-auto text-xs"
                            >
                              Analyze Content <ArrowUpRight className="w-3 h-3 ml-1" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
              
              {/* Expandable Posts Performance Section */}
              {engagementData.top_posts && engagementData.top_posts.length > 0 && (
                <div className="card-base overflow-hidden">
                  {/* Header - Always visible */}
                  <button
                    onClick={() => setPostsExpanded(!postsExpanded)}
                    className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-5 h-5 text-viraly-orange" />
                      <div className="text-left">
                        <h3 className="font-medium">Posts Performance</h3>
                        <p className="text-xs text-viraly-muted">
                          {engagementData.summary.total_posts} posts • Click to {postsExpanded ? 'collapse' : 'expand'} details
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* Quick stats when collapsed */}
                      {!postsExpanded && (
                        <div className="flex items-center gap-3 text-xs">
                          <span className="flex items-center gap-1 text-pink-400">
                            <Heart className="w-3 h-3" /> {engagementData.summary.total_likes}
                          </span>
                          <span className="flex items-center gap-1 text-blue-400">
                            <MessageCircle className="w-3 h-3" /> {engagementData.summary.total_comments}
                          </span>
                          <span className="flex items-center gap-1 text-green-400">
                            <Send className="w-3 h-3" /> {engagementData.summary.total_shares}
                          </span>
                        </div>
                      )}
                      {postsExpanded ? (
                        <ChevronUp className="w-5 h-5 text-viraly-muted" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-viraly-muted" />
                      )}
                    </div>
                  </button>
                  
                  {/* Expanded Content */}
                  {postsExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-white/10"
                    >
                      {/* Sort/Filter Controls */}
                      <div className="px-4 py-3 bg-white/5 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Filter className="w-4 h-4 text-viraly-muted" />
                          <span className="text-xs text-viraly-muted">Sort by:</span>
                          <select
                            value={postsSortBy}
                            onChange={(e) => setPostsSortBy(e.target.value)}
                            className="bg-white/10 border border-white/20 rounded px-2 py-1 text-xs"
                          >
                            <option value="engagement">Total Engagement</option>
                            <option value="likes">Most Likes</option>
                            <option value="comments">Most Comments</option>
                            <option value="date">Most Recent</option>
                          </select>
                        </div>
                        <span className="text-xs text-viraly-muted">
                          Showing {Math.min(postsPage * POSTS_PER_PAGE, engagementData.top_posts.length)} of {engagementData.top_posts.length}
                        </span>
                      </div>
                      
                      {/* Posts List */}
                      <div className="divide-y divide-white/10">
                        {engagementData.top_posts
                          .sort((a, b) => {
                            if (postsSortBy === 'likes') return (b.engagement?.likes || 0) - (a.engagement?.likes || 0);
                            if (postsSortBy === 'comments') return (b.engagement?.comments || 0) - (a.engagement?.comments || 0);
                            if (postsSortBy === 'date') return new Date(b.published_at || 0) - new Date(a.published_at || 0);
                            return (b.engagement?.total || 0) - (a.engagement?.total || 0);
                          })
                          .slice(0, postsPage * POSTS_PER_PAGE)
                          .map((post, idx) => (
                            <div 
                              key={post.id}
                              className="p-4 flex items-center gap-4 hover:bg-white/5 transition-colors cursor-pointer"
                              onClick={() => navigate(`/posts?tab=published`, { state: { highlightPostId: post.id } })}
                            >
                              {/* Rank Badge */}
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                idx === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                                idx === 1 ? 'bg-gray-400/20 text-gray-300' :
                                idx === 2 ? 'bg-orange-600/20 text-orange-400' :
                                'bg-white/10 text-viraly-muted'
                              }`}>
                                #{idx + 1}
                              </div>
                              
                              {/* Post Preview */}
                              {post.image_url ? (
                                <img src={post.image_url} alt="" className="w-14 h-14 rounded-lg object-cover" />
                              ) : (
                                <div className="w-14 h-14 rounded-lg bg-white/10 flex items-center justify-center">
                                  <FileText className="w-6 h-6 text-viraly-muted" />
                                </div>
                              )}
                              
                              {/* Post Info */}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {post.caption?.slice(0, 60)}{post.caption?.length > 60 ? '...' : ''}
                                </p>
                                <div className="flex items-center gap-3 mt-1 text-xs text-viraly-muted">
                                  <span className="capitalize">{post.platform}</span>
                                  {post.published_at && (
                                    <span>{new Date(post.published_at).toLocaleDateString()}</span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Engagement Stats */}
                              <div className="flex items-center gap-4 text-xs">
                                <div className="text-center">
                                  <p className="text-pink-400 font-bold">{post.engagement?.likes || 0}</p>
                                  <p className="text-viraly-muted">Likes</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-blue-400 font-bold">{post.engagement?.comments || 0}</p>
                                  <p className="text-viraly-muted">Comments</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-green-400 font-bold">{post.engagement?.shares || 0}</p>
                                  <p className="text-viraly-muted">Shares</p>
                                </div>
                                <div className="text-center px-3 py-1 bg-viraly-orange/10 rounded-lg">
                                  <p className="text-viraly-orange font-bold">{post.engagement?.total || 0}</p>
                                  <p className="text-viraly-muted">Total</p>
                                </div>
                              </div>
                              
                              <ChevronRight className="w-4 h-4 text-viraly-muted" />
                            </div>
                          ))}
                      </div>
                      
                      {/* Load More */}
                      {postsPage * POSTS_PER_PAGE < engagementData.top_posts.length && (
                        <div className="p-4 text-center border-t border-white/10">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPostsPage(p => p + 1)}
                            className="text-viraly-orange"
                          >
                            Load More Posts
                          </Button>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="card-base p-6 text-center">
              <BarChart3 className="w-10 h-10 mx-auto text-viraly-muted mb-3" />
              <p className="text-viraly-muted mb-2">No engagement data yet</p>
              <p className="text-xs text-viraly-muted mb-4">Publish posts to connected social accounts to track performance</p>
              <Button onClick={() => navigate("/create")} className="btn-primary">
                Create Your First Post
              </Button>
            </div>
          )}
        </motion.div>
        )}

        {/* How It Works - HIDDEN - Redundant now that users know the platform
            This section was taking up space. Can be re-enabled for new user onboarding if needed.
        */}
        {false && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-viraly-orange/10 via-purple-500/10 to-blue-500/10 border border-white/10"
          data-testid="how-it-works-section"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-viraly-gradient flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold">How It Works</h2>
                <p className="text-sm text-viraly-muted">Get started in 3 simple steps</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              onClick={() => setShowOnboarding(true)}
              className="text-viraly-orange hover:text-viraly-orange/80"
              data-testid="full-guide-btn"
            >
              Full Guide
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                step: 1,
                title: "Pick Your Source",
                desc: "Enter a URL, write text, or upload a photo/video",
                icon: Link2,
                color: "bg-blue-500/20 text-blue-400"
              },
              {
                step: 2,
                title: "AI Creates Content",
                desc: "We generate captions, images, and viral-optimized posts",
                icon: Sparkles,
                color: "bg-viraly-orange/20 text-viraly-orange"
              },
              {
                step: 3,
                title: "Review & Schedule",
                desc: "Edit, approve, and schedule to your social accounts",
                icon: Calendar,
                color: "bg-purple-500/20 text-purple-400"
              }
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-3 p-4 rounded-xl bg-white/5">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`}>
                  <item.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-viraly-orange">STEP {item.step}</span>
                  </div>
                  <h3 className="font-medium text-sm">{item.title}</h3>
                  <p className="text-xs text-viraly-muted">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions - Updated to new Create flow */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
            className="card-base p-6"
          >
            <h2 className="font-heading text-xl font-bold mb-4">Quick Actions</h2>
            <div className="space-y-3">
              {[
                { icon: Sparkles, label: "Auto-Pilot Mode", path: "/create?mode=autopilot", color: "bg-viraly-gradient", desc: "AI creates for you" },
                { icon: PenSquare, label: "Create Your Way", path: "/create?mode=manual", color: "bg-blue-500/20", desc: "Full control" },
                { icon: Calendar, label: "View Calendar", path: "/calendar", color: "bg-orange-500/20", desc: "Scheduled posts" },
                { icon: Layout, label: "Manage Posts", path: "/posts", color: "bg-purple-500/20", desc: "All your content" },
                { icon: UserCircle, label: "Manage Profiles", path: "/settings?tab=profiles", color: "bg-green-500/20", desc: "Brand profiles" }
              ].map((action) => (
                <Link
                  key={action.label}
                  to={action.path}
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-white/5 transition-colors group"
                  data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${action.color}`}>
                    <action.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <span className="block font-medium">{action.label}</span>
                    <span className="text-xs text-viraly-muted">{action.desc}</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-viraly-muted group-hover:text-white group-hover:translate-x-1 transition-all" />
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Recent Activity - Shows Published Posts */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.5 }}
            className="card-base p-6 lg:col-span-2"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold">Recent Activity</h2>
              <Link to="/posts?tab=published" className="text-viraly-orange text-sm hover:underline">
                View All Posted
              </Link>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-viraly-orange border-t-transparent rounded-full animate-spin" />
              </div>
            ) : activity.length > 0 ? (
              <div className="space-y-3">
                {/* Prioritize published/posted items, then approved, then scheduled */}
                {activity
                  .sort((a, b) => {
                    const statusOrder = { 'published': 0, 'approved': 1, 'scheduled': 2, 'pending': 3 };
                    return (statusOrder[a.status] || 4) - (statusOrder[b.status] || 4);
                  })
                  .slice(0, 5)
                  .map((post) => (
                  <div
                    key={post.id}
                    onClick={() => {
                      // Determine the correct tab based on status
                      let tab = 'scheduled';
                      if (post.status === 'published' || post.status === 'posted') {
                        tab = 'published';
                      } else if (post.status === 'approved') {
                        tab = 'approved';
                      } else if (post.status === 'scheduled' || post.status === 'pending') {
                        tab = 'scheduled';
                      }
                      navigate(`/posts?tab=${tab}&highlight=${post.id}`);
                    }}
                    className="flex items-center gap-4 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                    data-testid={`activity-item-${post.id}`}
                  >
                    {/* Thumbnail */}
                    {(post.image_url || post.video_url) && (
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
                        {post.video_url ? (
                          <video src={post.video_url} className="w-full h-full object-cover" muted />
                        ) : (
                          <img src={post.image_url} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                    )}
                    {!post.image_url && !post.video_url && (
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        post.platform === "facebook" ? "bg-blue-500/20" :
                        post.platform === "instagram" ? "bg-pink-500/20" :
                        "bg-black/20"
                      }`}>
                        <span className="text-xs font-bold uppercase">
                          {post.platform?.substring(0, 2)}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{post.caption?.substring(0, 50)}...</p>
                      <p className={`text-xs capitalize ${
                        post.status === 'published' ? 'text-green-400' :
                        post.status === 'approved' ? 'text-blue-400' :
                        'text-viraly-muted'
                      }`}>
                        {post.status === 'published' ? 'Published' : post.status}
                      </p>
                    </div>
                    {post.viral_score && (
                      <div className="flex items-center gap-1 text-viraly-orange">
                        <Zap className="w-4 h-4" />
                        <span className="text-sm font-bold">{post.viral_score}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-viraly-muted mb-4">No posts yet. Create your first content!</p>
                <Button 
                  onClick={() => navigate("/create")}
                  className="btn-primary"
                  data-testid="empty-state-create-btn"
                >
                  Create Post
                </Button>
              </div>
            )}
          </motion.div>
        </div>

        {/* Plan Status Banner */}
        {user?.plan === "free_trial" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.6 }}
            className="mt-6 glass-card p-6 flex flex-col md:flex-row items-center justify-between gap-4"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-viraly-gradient flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-heading font-bold">You're on the Trial Plan</p>
                <p className="text-viraly-muted text-sm">
                  {stats?.posts_remaining || 0} posts remaining • Upgrade for more posts & features
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost"
                onClick={handleShare}
                className="btn-secondary"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share & Earn
              </Button>
              <Button 
                onClick={() => navigate("/pricing")}
                className="btn-primary"
                data-testid="upgrade-btn"
              >
                Upgrade Now
              </Button>
            </div>
          </motion.div>
        )}

        {/* Referral/Loyalty Hint - Link to Rewards Center */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.7 }}
          className="mt-6 glass-card p-4 flex items-center justify-center gap-3"
        >
          <Gift className="w-5 h-5 text-viraly-orange" />
          <span className="text-sm">
            <span className="text-viraly-orange font-medium">Earn rewards</span>
            <span className="text-viraly-muted"> — Loyalty discounts, referral credits, and exclusive promotions!</span>
          </span>
          <a 
            href="/rewards"
            className="text-viraly-orange hover:underline text-sm font-medium flex items-center gap-1"
          >
            View Rewards <ChevronRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
      
      {/* Share & Earn Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareData={{
          title: "Viraly - AI Social Media Automation",
          text: user?.referral_code 
            ? `Join me on Viraly! Use my referral code: ${user.referral_code}`
            : "Join me on Viraly for AI-powered social media automation!",
          url: user?.referral_code 
            ? `${window.location.origin}?ref=${user.referral_code}`
            : window.location.origin
        }}
      />
      
      {/* Onboarding Guide */}
      <OnboardingGuide
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={() => setShowOnboarding(false)}
      />
    </DashboardLayout>
  );
}
